import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet';
import { getImageUrl, hideBrokenImage } from '../utils/imageUrl';

const BlogTag = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [tag, setTag] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTag = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/tags/${slug}`);
      setTag(response.data.tag || response.data.data?.tag);
      setBlogs(response.data.blogs || response.data.data?.blogs || []);
    } catch (error) {
      toast.error('Tag not found');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchTag();
  }, [fetchTag]);

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (!tag) {
    return <div className="text-center py-12">Tag not found</div>;
  }

  return (
    <>
      <Helmet>
        <title>#{tag.name} - Blog Tag</title>
        <meta name="description" content={`Posts tagged with ${tag.name}`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header Navigation */}
        <header className="bg-white shadow-sm border-b sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center">
                <img 
                  src="/fbrlogo1.svg" 
                  alt="FBR Logo" 
                  className="h-10 w-10 mr-3"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <Link to="/" className="text-2xl font-bold text-blue-600 hover:text-blue-700">
                  FBRSTAPP
                </Link>
              </div>
              <nav className="flex items-center space-x-6">
                <Link to="/" className="text-gray-600 hover:text-gray-900 font-medium">Home</Link>
                <Link to="/blog" className="text-gray-600 hover:text-gray-900 font-medium">Blog</Link>
                {user ? (
                  <Link
                    to="/app/dashboard"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </nav>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <div className="bg-purple-600 text-white py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/blog" className="hover:underline mb-2 inline-block">← Back to Blog</Link>
            <h1 className="text-4xl font-bold mb-2">#{tag.name}</h1>
            {tag.description && <p className="text-lg">{tag.description}</p>}
            <p className="text-sm mt-2">{blogs.length} post(s) tagged</p>
          </div>
        </div>

        {/* Blog Posts */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          {blogs.length > 0 ? (
            <div className="space-y-6">
              {blogs.map(blog => {
                const imageUrl = getImageUrl(blog.featured_image);
                return (
                <article key={blog.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition">
                  <div className="md:flex">
                    {imageUrl && (
                      <img
                        src={imageUrl}
                        alt={blog.title}
                        className="md:w-1/3 h-64 object-cover"
                        onError={hideBrokenImage}
                      />
                    )}
                    <div className="p-6 flex-1">
                      <Link
                        to={`/blog/${blog.slug}`}
                        className="text-2xl font-bold text-gray-800 hover:text-blue-600"
                      >
                        {blog.title}
                      </Link>
                      <div className="flex gap-4 text-gray-600 text-sm mt-2">
                        <span>By {blog.author_name}</span>
                        <span>{new Date(blog.publish_date).toLocaleDateString()}</span>
                        <span>{blog.views} views</span>
                      </div>
                      {blog.category_name && (
                        <Link
                          to={`/blog/category/${blog.category_slug}`}
                          className="inline-block mt-3 px-3 py-1 bg-blue-100 text-blue-600 rounded text-sm hover:bg-blue-200"
                        >
                          {blog.category_name}
                        </Link>
                      )}
                      <p className="mt-4 text-gray-600 line-clamp-3">
                        {blog.excerpt || blog.content.substring(0, 200)}...
                      </p>
                      <Link
                        to={`/blog/${blog.slug}`}
                        className="inline-block mt-4 text-blue-600 hover:text-blue-800 font-semibold"
                      >
                        Read More →
                      </Link>
                    </div>
                  </div>
                </article>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-600">
              No blog posts with this tag yet.
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogTag;
